import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useT } from '../lib/i18n'
import { IS_USTOOL } from '../lib/appKind'
import { Icon, type IconName } from './ui'
import { AskCompose, AskGlyph, useAsk } from './ask'

export interface TabDef {
  /**
   * Push a fresh `home` stamp on every tap, so the destination screen can
   * reset itself. Needed where the tab root holds flow state -- Book now --
   * because a push to the route you are already on does not remount it.
   */
  home?: boolean
  to: string
  /** i18n key for the label. */
  key: string
  icon: IconName
  /** Other routes this tab also owns. */
  also?: RegExp
  /** Show the unread dot here. */
  unread?: boolean
}

function owns(tab: TabDef, pathname: string): boolean {
  return tab.to === '/'
    ? pathname === '/'
    : pathname.startsWith(tab.to) || Boolean(tab.also?.test(pathname))
}

// Position eases in and out; the lens is a single smooth bump that is still
// ~40% up when the position arrives and decays to nothing with zero velocity.
// One motion -- there is no separate "landing" to overshoot.
const POS_MS = 300
const LENS_MS = 460
const easeInOut = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2)
const bump = (t: number) => Math.pow(Math.sin(Math.PI * Math.min(1, Math.max(0, t))), 1.5)

/**
 * The floating glass tab bar, with the Ask button at its side.
 *
 * Two capsules on one grid row: the tabs, and a circle for Ask. Tapping Ask
 * swaps the grid columns -- the tabs fold down to a single circle holding the
 * current tab's icon and the Ask circle grows into the compose box. The
 * answers rise above it in AskPanel. Tapping the folded circle brings the
 * tabs back.
 *
 * The selected-tab highlight is a LENS, not a fill: on a tab change it
 * lifts, stretches toward the new tab, magnifies whatever passes under it,
 * and eases down to rest over the new tab. Driven per frame in JS because
 * the magnification depends on where the lens is relative to each icon.
 */
export function LiquidTabBar({ tabs }: { tabs: TabDef[] }) {
  const msg = useT()
  const { pathname } = useLocation()
  const askState = useAsk()
  const n = tabs.length

  const activeIdx = Math.max(0, tabs.findIndex((t) => owns(t, pathname)))
  // The tint switches as the lens crosses halfway, not on the click -- so it
  // is state driven from the animation, not derived from the route.
  const [tinted, setTinted] = useState(activeIdx)

  // The field is unread_count. It was read as `unread`, which the API has
  // never returned, so this badge silently never appeared on the phone.
  const unread = useQuery<{ unread_count: number }>({
    queryKey: ['unread'], queryFn: () => api('/notifications?limit=1'),
  })
  const dot = (unread.data?.unread_count ?? 0) > 0

  const lens = useRef<HTMLDivElement>(null)
  const items = useRef<(HTMLAnchorElement | null)[]>([])
  const pos = useRef(activeIdx)      // where the lens is, fractional while moving
  const raf = useRef(0)

  const paint = useCallback((x: number, b: number) => {
    const el = lens.current
    if (!el) return
    // insetInlineStart, not left: the lens already has `inset-inline-start: 0`
    // and an inline width, so in RTL left+right+width over-constrain the box and
    // the browser discards `left` - the lens stayed under tab 0 in Urdu and Arabic.
    el.style.insetInlineStart = `${(x * 100) / n}%`
    el.style.transform = `scaleX(${1 + 0.55 * b}) scaleY(${1 + 0.16 * b})`
    el.classList.toggle('is-lens', b > 0.02)
    for (let j = 0; j < n; j++) {
      // 1 when the lens is centred on tab j, 0 a whole tab away.
      const overlap = Math.max(0, 1 - Math.abs(j - x))
      items.current[j]?.style.setProperty('--m', (1 + 0.34 * b * overlap).toFixed(3))
    }
  }, [n])

  const travel = useCallback((to: number) => {
    cancelAnimationFrame(raf.current)
    const from = pos.current
    if (from === to) { paint(to, 0); setTinted(to); return }
    const t0 = performance.now()
    let switched = false
    const step = (now: number) => {
      const e = Math.max(0, now - t0)
      const tp = Math.min(1, e / POS_MS)
      const tb = e / LENS_MS
      pos.current = from + (to - from) * easeInOut(tp)
      paint(pos.current, tb < 1 ? bump(tb) : 0)
      if (!switched && tp >= 0.5) { switched = true; setTinted(to) }
      if (e < LENS_MS) raf.current = requestAnimationFrame(step)
      else { pos.current = to; paint(to, 0) }
    }
    raf.current = requestAnimationFrame(step)
  }, [paint])

  useEffect(() => { travel(activeIdx) }, [activeIdx, travel])
  useEffect(() => () => cancelAnimationFrame(raf.current), [])

  // The compose box gets focus once the capsule has finished growing; a
  // caret appearing in a 60px circle that is still widening looks broken.
  useEffect(() => {
    if (!askState.open) return
    const t = setTimeout(() => askState.box.current?.focus({ preventScroll: true }), 420)
    return () => clearTimeout(t)
  }, [askState.open, askState.box])

  const inset = useKeyboardInset()

  return (
    <>
      <div className="tabbar-scrim" aria-hidden />
      <nav
        className={`tabbar ${askState.open ? 'is-asking' : ''}`}
        aria-label={msg('app.main')}
        style={inset ? { transform: `translateY(-${inset}px)` } : undefined}
      >
        <div className="tabbar-pill">
          <div className="tabbar-tabs" aria-hidden={askState.open}>
            <div ref={lens} className="tabbar-lens" style={{ width: `${100 / n}%` }} />
            {tabs.map((t, i) => (
              <Link
                key={t.to}
                to={t.to}
                state={t.home ? { home: Date.now() } : undefined}
                ref={(el) => { items.current[i] = el }}
                className="tabbar-tab"
                aria-current={i === activeIdx ? 'page' : undefined}
                data-on={i === tinted || undefined}
                tabIndex={askState.open ? -1 : 0}
                onClick={() => askState.setOpen(false)}
              >
                <span className="tabbar-ico">
                  <Icon name={t.icon} className="h-[23px] w-[23px]" />
                  {t.unread && dot && <span className="tabbar-dot" />}
                </span>
                <span className="tabbar-label">{msg(t.key)}</span>
              </Link>
            ))}
          </div>
          {/* The folded state: the current tab's icon, and the way back. */}
          <button
            type="button"
            className="tabbar-mini"
            tabIndex={askState.open ? 0 : -1}
            aria-label={msg('common.close')}
            onClick={() => askState.setOpen(false)}
          >
            <Icon name={tabs[activeIdx]?.icon ?? 'home'} className="h-[24px] w-[24px]" />
          </button>
        </div>

        <div className="tabbar-ask">
          <button
            type="button"
            className="tabbar-ask-glyph"
            aria-label={msg('askBubble.askAboutYourData')}
            aria-expanded={askState.open}
            /*
             * In Qafila this opens the mockup's journey sheet -- say where
             * you are going and it fills the request. In Ustool it opens the
             * lookup panel, which is what a carrier asks it: what is leaving
             * today, what his drivers owe him, which requests need a price.
             *
             * One button, the thing each app's person actually wants from it.
             */
            onClick={() => {
              if (!IS_USTOOL) { askState.setJourney(true); return }
              if (!askState.open) askState.setOpen(true)
              else askState.box.current?.focus()
            }}
          >
            <AskGlyph />
          </button>
          <div className="tabbar-ask-body">
            <AskCompose />
          </div>
        </div>
      </nav>
    </>
  )
}

/**
 * How far the on-screen keyboard has pushed the visual viewport up.
 *
 * On iOS a fixed element stays put in the LAYOUT viewport when the keyboard
 * opens, which puts a bottom bar underneath the keys. The visual viewport
 * reports the real gap; the bar and the panel translate up by it.
 */
export function useKeyboardInset(): number {
  const [inset, setInset] = useState(0)
  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return
    const read = () => {
      const gap = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop))
      // Anything under 80px is browser chrome settling, not a keyboard.
      setInset(gap > 80 ? gap : 0)
    }
    read()
    vv.addEventListener('resize', read)
    vv.addEventListener('scroll', read)
    return () => { vv.removeEventListener('resize', read); vv.removeEventListener('scroll', read) }
  }, [])
  return inset
}
